import { Injectable, Inject, PLATFORM_ID } from "@angular/core";
import { isPlatformBrowser } from "@angular/common";
import { BehaviorSubject } from "rxjs";

declare const posthog: any;

@Injectable({
  providedIn: "root",
})
export class ThemeService {
  private isDarkTheme = new BehaviorSubject<boolean>(false);
  isDarkTheme$ = this.isDarkTheme.asObservable();
  private readonly themeStorageKey = "theme";
  private isBrowser: boolean;

  constructor(@Inject(PLATFORM_ID) private platformId: object) {
    this.isBrowser = isPlatformBrowser(this.platformId);
    this.initTheme();
  }

  private initTheme(): void {
    if (this.isBrowser) {
      const storedPreference = localStorage.getItem(this.themeStorageKey);
      let darkMode: boolean;
      let themeSource: 'stored' | 'system' | 'default';

      if (storedPreference) {
        darkMode = storedPreference === "dark";
        themeSource = 'stored';
      } else {
        darkMode =
          window.matchMedia &&
          window.matchMedia("(prefers-color-scheme: dark)").matches;
        themeSource = 'system';
      }

      this.setTheme(darkMode);
      
      // Track initial theme preference
      this.trackInitialTheme(darkMode ? 'dark' : 'light', themeSource);

      // Listen for OS theme changes (safely, for older browsers)
      const mql = window.matchMedia("(prefers-color-scheme: dark)");

      const handleChange = (e: MediaQueryListEvent) => {
        // Only update if no manual preference is stored
        if (!localStorage.getItem(this.themeStorageKey)) {
          this.setTheme(e.matches);
        }
      };

      // Some older mobile browsers only implement addListener/removeListener
      if ("addEventListener" in mql) {
        mql.addEventListener("change", handleChange);
      } else if ("addListener" in mql) {
        // @ts-expect-error - older spec
        mql.addListener(handleChange);
      }
    } else {
      // Default theme for server-side rendering (optional, could be based on a cookie or config)
      this.setTheme(false); // Default to light theme or your preferred default
    }
  }

  private setTheme(isDark: boolean): void {
    this.isDarkTheme.next(isDark);
    if (this.isBrowser) {
      if (isDark) {
        document.documentElement.classList.add("dark");
      } else {
        document.documentElement.classList.remove("dark");
      }
    }
  }

  toggleTheme(): void {
    this.setDarkTheme(!this.isDarkTheme.value);
  }

  /** Set theme explicitly and persist. */
  setDarkTheme(isDark: boolean): void {
    if (this.isDarkTheme.value === isDark) return;
    const newTheme = isDark ? "dark" : "light";
    this.setTheme(isDark);
    if (this.isBrowser) {
      localStorage.setItem(this.themeStorageKey, newTheme);
      this.trackThemeChange(newTheme);
    }
  }

  /**
   * Track initial theme preference when app loads
   */
  private trackInitialTheme(theme: 'dark' | 'light', source: 'stored' | 'system' | 'default'): void {
    try {
      if (
        typeof posthog !== 'undefined' && 
        posthog !== null &&
        typeof posthog.capture === 'function'
      ) {
        posthog.capture('theme_initialized', {
          theme: theme,
          source: source,
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight
        });
      }
    } catch (error) {
      console.error('Error tracking initial theme:', error);
    }
  }

  /**
   * Track theme changes in PostHog
   */
  private trackThemeChange(theme: 'dark' | 'light'): void {
    try {
      if (
        typeof posthog !== 'undefined' && 
        posthog !== null &&
        typeof posthog.capture === 'function'
      ) {
        posthog.capture('theme_changed', {
          theme: theme,
          previous_theme: theme === 'dark' ? 'light' : 'dark',
          timestamp: new Date().toISOString(),
          user_agent: navigator.userAgent,
          screen_width: window.innerWidth,
          screen_height: window.innerHeight
        });
      }
    } catch (error) {
      console.error('Error tracking theme change:', error);
    }
  }
}
